import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Search, Trash2, Loader2, ShieldOff, Upload } from 'lucide-react';
import { toast } from 'sonner';

const REASON_LABELS = { opt_out: 'Opt-out', spam_report: 'Spam', manual: 'Reczne', ai_detected: 'AI', bounce: 'Bounce' };

export default function BlacklistPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ phone: '', email: '', reason: 'manual' });
  const [saving, setSaving] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteImporting, setPasteImporting] = useState(false);
  const [pasteReport, setPasteReport] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const r = await api.get('/blacklist', { params: { search } });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch {} finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!form.phone && !form.email) { toast.error('Podaj numer lub email'); return; }
    setSaving(true);
    try {
      await api.post('/blacklist', form);
      toast.success('Dodano do czarnej listy');
      setDialogOpen(false);
      setForm({ phone: '', email: '', reason: 'manual' });
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Usunac z czarnej listy?')) return;
    try { await api.delete(`/blacklist/${id}`); toast.success('Usunieto'); fetch(); }
    catch { toast.error('Blad'); }
  };

  const handlePasteImport = async () => {
    if (!pasteText.trim()) { toast.error('Wklej dane'); return; }
    setPasteImporting(true);
    setPasteReport(null);
    try {
      const r = await api.post('/blacklist/paste-import', { text: pasteText });
      setPasteReport(r.data);
      toast.success(`Dodano ${r.data.added} do czarnej listy`);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setPasteImporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="blacklist-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Czarna lista</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} zablokowanych kontaktow</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setPasteOpen(true); setPasteText(''); setPasteReport(null); }} data-testid="blacklist-paste-button">Wklej dane</Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="blacklist-add-button"><Plus className="h-4 w-4 mr-2" />Dodaj do listy</Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Szukaj..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card/50" data-testid="blacklist-search-input" />
        </div>
      </div>

      <Card className="border-border/50 bg-card/80">
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div> : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Telefon</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Powod</TableHead>
                  <TableHead>Data dodania</TableHead>
                  <TableHead className="w-20">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    <ShieldOff className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                    Czarna lista jest pusta
                  </TableCell></TableRow>
                ) : items.map(item => (
                  <TableRow key={item.id} className="hover:bg-muted/30 transition-colors duration-150">
                    <TableCell className="font-mono text-sm">{item.phone || '-'}</TableCell>
                    <TableCell className="text-sm">{item.email || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{REASON_LABELS[item.reason] || item.reason}</Badge></TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{item.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleRemove(item.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Dodaj do czarnej listy</DialogTitle><DialogDescription>Zablokuj kontakt</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Numer telefonu</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+48..." data-testid="blacklist-form-phone" /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="blacklist-form-email" /></div>
            <p className="text-xs text-muted-foreground">Kontakt z numerem lub emailem na czarnej liscie nigdy nie otrzyma wiadomosci z zadnej kampanii.</p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="blacklist-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Dodaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paste Import Dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Wklej dane do czarnej listy</DialogTitle>
            <DialogDescription>Szybki import (max 500 rekordow)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Wklej emaile/telefony (jeden na linie):</Label>
                <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={10}
                  className="w-full rounded-lg border border-input bg-background/50 px-3 py-2 text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder={"spam@example.com\n+48500000001\nniechce@firma.pl;+48500000002"}
                  data-testid="blacklist-paste-textarea" />
                <p className="text-[10px] text-muted-foreground">{pasteText.split('\n').filter(l => l.trim()).length} rekordow</p>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs font-medium mb-2">Formaty:</p>
                  <div className="space-y-1 text-[11px] text-muted-foreground font-mono">
                    <p>spam@example.com</p>
                    <p>+48500000001</p>
                    <p>niechce@firma.pl;+48500000002</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs font-medium mb-2">Struktura CSV:</p>
                  <div className="text-[11px] text-muted-foreground font-mono">
                    <p className="text-[hsl(var(--destructive))]">telefon;email;powod</p>
                    <p>+48500000001;spam@ex.com;opt_out</p>
                    <p>+48500000002;;spam_report</p>
                  </div>
                </div>
                {pasteReport && (
                  <div className="p-3 rounded-lg bg-[hsl(var(--destructive))]/5 border border-[hsl(var(--destructive))]/20 space-y-1">
                    <p className="text-xs font-medium">Raport:</p>
                    <p className="text-xs">Dodanych: <strong className="text-[hsl(var(--destructive))]">{pasteReport.added}</strong></p>
                    <p className="text-xs">Duplikatow: <strong>{pasteReport.duplicates}</strong></p>
                    <p className="text-xs">Bledow: <strong>{pasteReport.errors}</strong></p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPasteOpen(false)}>Zamknij</Button>
            <Button variant="destructive" onClick={handlePasteImport} disabled={pasteImporting || !pasteText.trim()} data-testid="blacklist-paste-submit">
              {pasteImporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldOff className="h-4 w-4 mr-2" />}
              Zablokuj ({pasteText.split('\n').filter(l => l.trim()).length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
